/* eslint-disable no-promise-executor-return */
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import type {
	IAPICompany,
	IAPIJobForUser,
	IAPIJobPreviewForUser,
	IAPIOtherTerms,
	IAPILoginUser,
	IAPIMatchForJobSeekerListEntry,
	IAPIMatchForJobSeeker,
	IAPICompanySnippet
} from '@hokify/common';
import { convertNumber } from '~/helpers/utility';
import { getSocialMediaMetaData } from '~/helpers/metadata';
import { getAppDownloadLink } from '~/helpers/user-agent';
import { PageNotFoundError } from '~/errors/PageNotFoundError';
import type {
	ISendjobData,
	IAvailableFilters,
	ISelectedFilter,
	IJobalarmData
} from '~/store/types';
import { setLastJobSearchCookie } from '~/helpers/cookie';
import PopupHandler from '~/components/website/PopupHandler.vue';
import JobList from '@hokify/shared-components/lib/components/JobList.vue';
import type { jobalarmEventLabel } from '~/types/jobalarm';
import type { ComponentPublicInstance } from 'vue';

const jobsStore = namespace('jobs');
const locationStore = namespace('location');
const valuesStore = namespace('values');
const userStore = namespace('user/profile');
const loginStore = namespace('login');
const relationsStore = namespace('relations');
const matchesStore = namespace('matches');
const gncStore = namespace('gnc');

function hasBlur(obj: any): obj is HTMLElement {
	return typeof obj?.blur === 'function';
}

function isFocused(obj: any) {
	return !!obj?.focused;
}

interface IJobSearchRequest {
	branch?: string;
	city?: string;

	filters?: string[];
	lastView?: string;
	template?: string;

	limit?: number;

	isCompanyPage?: boolean;
	page?: number;
	experimental?: boolean;
	radius?: number;

	clientInteraction: boolean | undefined;
}

interface IJobSearchViaUrlRequest {
	url: string;
	page?: number;
	clientInteraction: boolean | undefined;
	experimental?: boolean;
	radius?: string;
}

function isHTMLElement(element: any): element is HTMLElement {
	return element && element instanceof HTMLElement;
}

@Component({
	watchQuery: ['branch', 'city', 'filters', 'radius'],
	scrollToTop: true,
	validate({ route, store }) {
		return !route?.meta?.some(
			metaTag => metaTag.domain && metaTag.domain !== store.state.topLevelDomain
		);
	},
	components: {
		Autocomplete: () => import('@hokify/shared-components/lib/components/Autocomplete.vue'),
		ErrorBox: () => import('@hokify/shared-components/lib/components/ErrorBox.vue'),
		HokSelect: () => import('@hokify/shared-components/lib/components/HokSelect.vue'),
		Pagination: () => import('@hokify/shared-components/lib/components/Pagination.vue'),
		JobFilterMobile: () => import('~/components/website/jobs/JobFilterMobile.vue'),
		JobFilterWrapper: () => import('~/components/website/jobs/JobFilterWrapper.vue'),
		NotificationBox: () => import('~/components/website/NotificationBox.vue'),
		PopupHandler,
		JobList // don't use async import nor import wrappers with functional components
	},
	head(this: Joblist) {
		let indexing: 'noindex' | 'index' = 'index';
		if ((this.version || this.apiData?.pageSettings?.noIndex) && !this.isJobOverviewPage) {
			indexing = 'noindex';
		}

		let deepLink;
		if (indexing === 'index' && this.apiData?.lp) {
			deepLink =
				this.canonicalUrl ||
				`hokify://jobs/${this.apiData?.path?.pageType}${
					this.apiData?.path.url
				}?region=${this.apiData?.geoRegion?.toLowerCase()}`;
		}

		const result: any = {
			title: this.pageTitle,
			titleTemplate: '%s | hokify',
			link: this.headerLinks,
			meta: [
				{ hid: 'robots', name: 'robots', content: `${indexing}, follow` },
				{ hid: 'description', name: 'description', content: this.pageDescription }
			]
		};

		result.meta.push(
			...getSocialMediaMetaData(
				result,
				this.$store.state.topLevelDomain,
				undefined,
				deepLink,
				this.$route.query,
				undefined,
				this.socialsTitle,
				this.socialsDescription,
				this.socialsImage
			)
		);
		return result;
	},
	async asyncData({ error, route, store, query, params, res }) {
		// do not apply advanced logic if only the mobile filter page gets opened
		if (
			route.hash &&
			(route.hash.includes('#jobfilterpage') || route.hash.includes('#jobsearch'))
		) {
			return;
		}
		try {
			let { result } = params as any;
			let lastJobPageNumber;
			const { path } = route;
			/*
			Check if current url and beautifiedUrl are the same (works for /jobs because no beautifiedUrl is set)
			only then get last visited page number from store
			*/
			if (path === store.state.jobs.lastJobPageNumber?.beautifiedUrl) {
				lastJobPageNumber = store.state.jobs.lastJobPageNumber?.page;
			}

			if (!result) {
				// for company search
				if (path && /\/c\/.*\/search/.test(path) && params.company) {
					result = await store.dispatch('jobs/loadJobsSearch', {
						branch: (query.branch as string) || undefined,
						city: (query.city as string) || undefined,
						filters: [`company-${params.company}`, ...((query.filters as string[]) || [])],
						radius: query.radius ? parseInt(query.radius as string, 10) : undefined,
						isCompanyPage: true,
						clientInteraction: process.client
					} satisfies IJobSearchRequest);
					// set company search results on noIndex on default
					result.pageSettings.noIndex = true;
					// job search with active quick filters
				} else if (
					(!path || path === '/jobs/search' || path === '/jobs') &&
					(query.branch || query.city || query.filters)
				) {
					result = await store.dispatch('jobs/loadJobsSearch', {
						branch: (query.branch as string) || undefined,
						city: (query.city as string) || undefined,
						filters: (query.filters as string[]) || undefined,
						radius:
							query.city && query.radius && typeof query.radius === 'string'
								? parseInt(query.radius, 10)
								: undefined,
						clientInteraction: process.client
					} satisfies IJobSearchRequest);
					// regular job search
				} else if (path && path !== '/jobs/search') {
					result = await store.dispatch('jobs/loadJobsUrl', {
						url: decodeURI(path),
						page: lastJobPageNumber,
						clientInteraction: process.client,
						radius: query.radius && typeof query.radius === 'string' ? query.radius : undefined
					} satisfies IJobSearchViaUrlRequest);
				} else {
					result = await store.dispatch('jobs/loadJobsUrl', {
						url: '/jobs',
						page: lastJobPageNumber,
						clientInteraction: process.client
					} satisfies IJobSearchViaUrlRequest);
				}
			}

			if (lastJobPageNumber) {
				// reset last visited job page number again
				await store.dispatch('jobs/updateLastJobPageNumber', { page: 0, beautifiedUrl: '' });
			}

			let { relationCount } = result;
			if (relationCount.includes('+')) {
				relationCount = relationCount.slice(0, -1);
			}

			const cvSearchCards = [
				{
					id: 'lust_dich_neu_zu_erfinden',
					headline: 'Lust dich neu zu erfinden?',
					firstLine: 'Wir analysieren in nur 3 Minuten deinen Lebenslauf',
					secondLine: 'Du erhältst für dich passende Jobvorschläge!'
				},
				{
					id: 'welcher_job_bist_du',
					headline: 'Welcher Job bist du?',
					firstLine: 'Wir analysieren in nur 3 Minuten deinen Lebenslauf',
					secondLine: 'Du erhältst für dich passende Jobvorschläge!'
				}
			];

			// USP ad cards for list
			const uspCards = [
				{
					headline: 'Ein Profil. <br />Alle Jobs',
					firstLine: 'Einmal erstellen, für alle Jobs nutzen',
					secondLine: '60% schneller bei jeder hokify Bewerbung'
				},
				{
					headline: 'Entspannte <br />Jobsuche.',
					firstLine: 'Kein Anschreiben notwendig',
					secondLine: 'In nur 3 Minuten bewerben'
				},
				{
					headline: 'Einfache Bewerbung <br />via Smartphone',
					firstLine: 'Traumjob finden, egal wo du gerade bist',
					secondLine: 'Lebenslauf direkt am Handy erstellbar'
				}
			];

			const returnValue: any = {
				availableFilters: params.availableFilters || result.availableFilters || {},
				currentPage: result.page || (result.relationCount > 0 ? 1 : 0),
				currentSelectedFilters: result.selected?.filters?.length
					? result.selected.filters.map(a => ({ filter: a.name, title: a.formatted, url: a.url }))
					: [],
				relations: result.relations,
				jobListDebug: result.jobListDebug,
				pageCount: Math.ceil(relationCount / 15) || 1,
				initialJobField: result.selected?.branch?.formatted || '',
				initialLocationName: result.selected?.city?.formatted || '',
				currentJobField: result.selected?.branch?.formatted || '',
				currentLocationName: result.selected?.city?.formatted || '',
				enableNotifications: result.path?.pageType !== 'none',
				showCurrentPage: !!(result.page > 1),
				lastResultUrl: route.fullPath, // result.beautifiedUrl || '/jobs',
				// TODO remove path check when de layout is changed to new jobsearch design
				showFilters: (path.startsWith('/c') && result.selected?.filters?.length > 1) || false,
				// check if there is a static SEO text for current joblist LP
				currentRadius:
					result.selected?.city?.formatted && query.radius && typeof query.radius === 'string'
						? `${parseInt(query.radius as string, 10)} km`
						: '',
				showInfoBox: query?.version === 'lpad2',
				uspCards,
				cvSearchCard: cvSearchCards[Math.floor(Math.random() * 2)]
			};

			// only generate last-jobsearch cookie if we got beautifiedUrl and job results
			if (result.beautifiedUrl && result.relations.length > 0) {
				setLastJobSearchCookie(result.beautifiedUrl, res);
			}
			returnValue.version = query.version;
			returnValue.apiData = result;
			if (result.otherTerms) {
				returnValue.otherTerms = result.otherTerms;
				store.commit('jobs/setOtherTerms', result.otherTerms);
			}

			// temporary workaround
			if (result.alternatives?.length && !path.startsWith('/c/')) {
				returnValue.error = {
					title: 'Keine Jobs gefunden',
					alternatives: result.alternatives
						.map(altWrapper =>
							altWrapper.results.map(alt => ({
								city: alt.city,
								branch: alt.branch,
								url: alt.canonicalUrl
							}))
						)
						.reduce((a, b) => a.concat(b), [])
				};
			}

			await store.dispatch('user/landingpage/loadLandingPageData');

			return returnValue;
		} catch (err: any) {
			const returnValue: any = {
				availableFilters: {},
				currentPage: 0,
				currentSelectedFilters: [],
				relations: [],
				pageCount: 0,
				initialJobField: query.branch || '',
				initialLocationName: query.city || '',
				currentJobField: query.branch || '',
				currentLocationName: query.city || '',
				enableNotifications: false
			};

			if (err?.response?.data?.otherTerms) {
				returnValue.otherTerms = err.response.data.otherTerms;
			}

			if (params?.company && err?.response?.status === 410) {
				if (process.server) {
					// res is only available on server side
					res.statusCode = err.response.status; // = 410
				}
				returnValue.companyGone = true;
				returnValue.apiData = { similarCompanies: [] };

				if (err?.response?.data?.similarCompanies) {
					returnValue.apiData.similarCompanies = err.response.data.similarCompanies;
				}

				return returnValue;
			}

			if (err?.response?.status === 404 && err?.response?.data?.code === 'INVALID_LOCATION') {
				if (process.server) {
					// res is only available on server side
					res.statusCode = err.response.status; // = 404
				}
				const e = { title: 'Ort konnte nicht gefunden werden', alternatives: [] };
				if (err?.response.data?.alternatives?.length > 0) {
					e.alternatives = err?.response.data?.alternatives;
				}

				returnValue.error = e;
				return returnValue;
			}

			if (err?.response?.status === 404) {
				error(new PageNotFoundError());
				return;
			}

			error(err);
		}
	},
	async beforeRouteLeave(this: Joblist, to, from, next) {
		if (
			this.$isMobile.any ||
			from.path.startsWith('/c/') ||
			!to.path.startsWith('/job/') ||
			!to.params?.job
		) {
			next();
			return;
		}
		this.$router.push({
			path: from.path,
			params: from.params,
			query: from.query,
			hash: `#job-${to.params?.job}`
		});
		next(false);
	},
	async beforeRouteUpdate(this: Joblist, to, _from, next) {
		let jobNr;
		if (to.hash.startsWith('#job-')) {
			jobNr = parseInt(to.hash?.substr('#job-'.length), 10);
			// if we are on a mobile device redirect to jobdetail page instead
			if (this.$isMobile.phone) {
				this.$router.push(`/job/${jobNr}`);
				return;
			}

			if (jobNr && this.jobDetail?.jobNr !== jobNr) {
				await this.loadJobData(jobNr);
			}
		}
		next();
	},

	async beforeRouteEnter(_to, from, next) {
		let fromRoute = 'default' as 'default' | 'gnc' | 'startpage';
		// from.name because on refresh from.path is always set '/' which causes the element
		if (from.name === 'index') {
			fromRoute = 'startpage';
		} else if (from.name === 'lp-brutto-netto-rechner') {
			fromRoute = 'gnc';
		}
		next(vm => (vm as InstanceType<typeof Joblist>).setFromRoute(fromRoute));
	}
})
export default class Joblist extends Vue {
	// actions to load jobs
	@jobsStore.Action('loadJobsSearch') loadJobsSearch;

	@jobsStore.Action('updateLastJobPageNumber') updateLastJobPageNumber;

	@jobsStore.State('lastJobPageNumber') lastJobPageNumber;

	@userStore.State('obj') user?: IAPILoginUser;

	// actions for autocomplete views
	@locationStore.Action('resolveGeoCoords') resolveGeoCoords;

	@valuesStore.Action('ontologyKeywordSuggestion') ontologyKeywordSuggestion;

	// branch suggestions without companies
	@valuesStore.Action('ontologySuggestion') ontologySuggestion;

	@valuesStore.Action('companyLocationSuggestions') companyLocationSuggestions;

	// action to trigger jobclick for LPI
	@jobsStore.Action('jobItemClicked') jobItemClicked;

	@jobsStore.Action('loadJobDetail') loadJobDetail;

	@relationsStore.Action('saveRelation') saveRelation;

	@relationsStore.Action('discardRelation') discardRelation;

	@matchesStore.Getter('getMatchByRelation') getMatchByRelation;

	@matchesStore.Action('getUserMatchByRelation') getUserMatchByRelation;

	@matchesStore.Getter('getStatusOfRelation') getStatusOfRelation;

	@matchesStore.Action('deleteMatch') deleteMatch;

	@loginStore.State('loggedIn') loggedIn;

	@matchesStore.Action('getSaved') getSaved!: (
		forceReload?: boolean
	) => Promise<IAPIMatchForJobSeekerListEntry[]>;

	@matchesStore.Getter('getAll') getAll;

	@Ref('jobPopupHandler') readonly jobPopupHandler?: { handleClick: () => void };

	@gncStore.Action('sendGnc') sendGnc;

	observer?: IntersectionObserver;

	error: any = false;

	availableFilters!: IAvailableFilters;

	currentEmail?: string;

	currentPhone?: string;

	currentPage = 1;

	enableNotifications = false;

	jobDetail?: IAPIJobForUser | null = null;

	additionalJobData?: {
		cityCanonical?: string;
		wage?: {
			value: number;
			unit: 'HOUR' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
		};
		type?: string;
	} | null = null;

	additionalSettings = {};

	jobOverviewLps = [];

	similarJobs: { job: IAPIJobPreviewForUser }[] = [];

	rootFields: string[] = [];

	useJobFields = false;

	match?: IAPIMatchForJobSeeker;

	showJobDetailMeta = false;

	cvSearchCard = {
		id: 'lust_dich_neu_zu_erfinden',
		headline: 'Lust dich neu zu erfinden?',
		firstLine: 'Wir analysieren in nur 3 Minuten deinen Lebenslauf',
		secondLine: 'Du erhältst für dich passende Jobvorschläge!'
	};

	uspCards = [
		{
			headline: 'Ein Profil. <br />Alle Jobs',
			firstLine: 'Einmal erstellen, für alle Jobs nutzen',
			secondLine: '60% schneller bei jeder hokify Bewerbung'
		},
		{
			headline: 'Entspannte <br />Jobsuche.',
			firstLine: 'Kein Anschreiben notwendig',
			secondLine: 'In nur 3 Minuten bewerben'
		},
		{
			headline: 'Einfache Bewerbung <br />via Smartphone',
			firstLine: 'Traumjob finden, egal wo du gerade bist',
			secondLine: 'Lebenslauf direkt am Handy erstellbar'
		}
	];

	data() {
		return {
			currentEmail: undefined,
			currentPhone: undefined,
			gncCalculation: undefined,
			match: undefined,
			showInfoBox: false,
			uspCards: undefined
		};
	}

	created() {
		this.currentEmail = this.user?.general?.email;
		this.currentPhone = this.user?.general?.phone;
	}

	get isJobOverviewPage() {
		return (
			this.$route.path !== '/jobs/search' &&
			!this.apiData?.path?.url &&
			this.apiData?.path?.pageType === 'none'
		);
	}

	get jobAlarmFormData(): IJobalarmData {
		return {
			phone: this.currentPhone ? this.currentPhone : undefined,
			email: this.currentEmail ? this.currentEmail : undefined,
			search: this.searchTerm ? { searchterm: this.searchTerm } : undefined,
			location: this.apiData?.selected?.city?.formatted
				? { address: this.apiData?.selected?.city?.formatted }
				: undefined,
			region: this.apiData?.geoRegion?.toLowerCase() || 'at',
			radius: parseInt(this.currentRadius, 10) || undefined,
			filters: this.apiData?.selected?.filters?.map(filter => filter.name) || []
		};
	}

	get sendJobFormData(): ISendjobData {
		return {
			email: this.currentEmail,
			jobName: this.jobDetail?.name,
			jobNr: this.jobDetail?.jobNr,
			companyName: this.jobDetail?.company?.name
		};
	}

	get currentData() {
		switch (this.mode) {
			case 'login':
			case 'sendjob':
				return this.sendJobFormData;
			case 'jobalarm':
			default:
				return this.jobAlarmFormData;
		}
	}

	get searchTerm() {
		if (this.useJobFields) {
			return this.jobDetail?.fields?.[0]?.name;
		}
		if (this.apiData?.selected?.branch?.formatted) {
			return this.apiData?.selected?.branch?.formatted;
		}
	}

	jobgone = false;

	relations!: (
		| { type: 'company'; obj: IAPICompanySnippet }
		| { type: 'job'; obj: IAPIJobPreviewForUser }
	)[];

	jobListDebug!: any[];

	loading = false;

	pageCount!: number;

	initialJobField!: string;

	currentJobField!: string;

	initialLocationName!: string;

	currentLocationName!: string;

	currentSelectedFilters: ISelectedFilter[] = [];

	showBar = false;

	showCurrentPage = false;

	showFilters = false;

	apiData: any = null;

	otherTerms!: IAPIOtherTerms;

	isCompanyPage = false;

	mode = 'login';

	version?: string; // for lpad (landingpage ad)

	lastResultUrl!: string;

	currentRadius = '';

	radiusOptions = [
		{ id: 10, value: '10 km' },
		{ id: 20, value: '20 km' },
		{ id: 30, value: '30 km' },
		{ id: 50, value: '50 km' }
	];

	jobalarmTriggered = false;

	currentEventLabel?: jobalarmEventLabel;

	scrollDown = false;

	scrollUp = false;

	startY;

	gncCalculation: {} | undefined;

	handleScroll() {
		const { scrollY } = window;
		if (scrollY > 210) {
			if (scrollY > this.startY) {
				this.scrollDown = true;
				this.scrollUp = false;
			} else if (scrollY < this.startY) {
				this.scrollDown = false;
				this.scrollUp = true;
			}
			this.startY = scrollY;
		}
	}

	radius() {
		return this.radiusOptions;
	}

	setRadius(event) {
		if (event.value === 'Zurücksetzen') {
			if (this.radiusOptions.length > 4) {
				this.radiusOptions.pop();
			}
			this.currentRadius = '';
		} else {
			this.currentRadius = event.value;
			if (this.radiusOptions.length < 5) {
				this.radiusOptions.push({ id: 0, value: 'Zurücksetzen' });
			}
		}
		this.handleJobSearch(true);
	}

	setLocationName(locationPlaintext: string) {
		this.currentLocationName = locationPlaintext;
		if (
			this.currentLocationName.length === 0 ||
			this.currentLocationName !== this.initialLocationName
		) {
			this.currentRadius = '';
			if (this.radiusOptions.length > 4) {
				this.radiusOptions.shift();
			}
		}
	}

	get company(): IAPICompany {
		return this.apiData?.company;
	}

	get headerLinks() {
		const path = this.canonicalUrl;
		if (path) {
			return [
				{
					hid: 'canonical',
					rel: 'canonical',
					href: path
				}
				// ...getAllAlternateUrls(path) // no alternates in other countries
			];
		}
		return [];
	}

	get canonicalUrl(): string | undefined {
		if (this.isJobOverviewPage) {
			// special case for "new" jobs pages (/jobs)
			return `${this.$store.state.host}/jobs`;
		}

		if (!this.apiData?.pageSettings?.canonicalUrl) {
			return undefined;
		}
		return `${this.$store.state.host}${this.apiData?.pageSettings?.canonicalUrl}`;
	}

	async mounted() {
		this.loading = true;
		try {
			await this.init();
		} finally {
			this.loading = false;
		}
	}

	// trigger init on route param change
	@Watch('$route.query')
	routeChanged() {
		this.$nextTick(() => {
			this.loading = true;
			try {
				this.init();
			} finally {
				this.loading = false;
			}
		});
		this.showJobDetailMeta = true;
	}

	async init() {
		this.registerIntersectionObserver();
		if (this.radiusOptions.length === 4 && this.currentRadiusId) {
			// only add if a radius is selected
			this.radiusOptions.push({ id: 0, value: 'Zurücksetzen' });
		}
		if (this.$route.hash.includes('jobgone')) {
			this.jobgone = true;
		} else if (
			!this.version &&
			this.apiData?.beautifiedUrl &&
			decodeURI(this.apiData?.beautifiedUrl) !== this.$route.path.split('?')[0]
		) {
			this.$router.replace(decodeURI(this.apiData?.beautifiedUrl));
		}

		if (this.$route.hash?.startsWith('#job-')) {
			const jobNr = parseInt(this.$route.hash?.substr('#job-'.length), 10);
			// if we are on a mobile device redirect to jobdetail page instead
			if (this.$isMobile.phone) {
				this.$router.push(`/job/${jobNr}`);
				return;
			}

			if (jobNr && this.jobDetail?.jobNr !== jobNr) {
				await this.loadJobData(jobNr);
			}
		}

		if (this.isCompanyPage) {
			this.jobDetail = null;
			const jobs = this.relations.filter(j => j.type === 'job');
			this.$trackWebsiteEvent('company_view', {
				companyId: (this.currentPage === 1 && this.company._id) || undefined,
				jobs: jobs.map(j => ({ _id: j.obj._id, jobNr: (j.type === 'job' && j.obj.jobNr) || 0 })),
				branch: this.currentJobField,
				topfields: this.apiData?.rootFields,
				jobfields: this.apiData?.allFields,
				keywords: this.apiData?.trackingKeywords,
				topLevelDomain: this.$store.state.topLevelDomain
			});
		} else {
			const jobs = this.relations.filter(j => j.type === 'job');
			this.$trackWebsiteEvent('joblist_view', {
				lpId: (this.currentPage === 1 && this.apiData?.lpId) || undefined,
				jobs:
					jobs.map(j => ({ _id: j.obj._id, jobNr: (j.type === 'job' && j.obj.jobNr) || 0 })) || [],
				branch: this.currentJobField,
				topfields: this.apiData?.rootFields,
				jobfields: this.apiData?.allFields,
				keywords: this.apiData?.trackingKeywords,
				topLevelDomain: this.$store.state.topLevelDomain,
				is_logged_in: this.$store.state.login.loggedIn ? 1 : 0
			});
		}
	}

	registerIntersectionObserver() {
		this.observer = new IntersectionObserver(this.isVisible);
		this.observeElements();
	}

	observeElements() {
		const element = document.querySelector('#jobalarm');
		if (element) {
			this.observer?.observe(element);
		}
	}

	beforeDestroy() {
		this.observer?.disconnect();
	}

	isVisible(entries) {
		this.showBar = !entries.some(entry => entry.isIntersecting);
	}

	get appstoreLink(): string {
		// TODO deeplink into app https://app.asana.com/1/60604373080989/project/1207203365740563/task/1209948149357431?focus=true
		// if (this.apiData?.path?.pageType && this.apiData.path.pageType !== 'none') {
		// 	return `https://67ra.adj.st/jobs/${this.apiData?.path?.pageType}${
		// 		this.apiData.path.path
		// 	}?region=${this.$store.state.topLevelDomain.toUpperCase()}&adjust_t=o4bwle&adjust_deeplink=${encodeURIComponent(
		// 		`hokify://jobs/${this.apiData?.path?.pageType}${
		// 			this.apiData.path.path
		// 		}?region=${this.$store.state.topLevelDomain.toUpperCase()}`
		// 	)}`;
		// }

		if (process.server) {
			if (this.$isMobile.apple) {
				return getAppDownloadLink('ios');
			}

			return getAppDownloadLink('android');
		}

		return getAppDownloadLink();
	}

	resetFilters() {
		this.currentSelectedFilters = [];
		this.handleJobSearch();
	}

	toggleSelectedFilters(filter: ISelectedFilter) {
		if (this.currentSelectedFilters.map(a => a.filter).includes(filter.filter)) {
			this.currentSelectedFilters = this.currentSelectedFilters.filter(
				item => item.filter !== filter.filter
			);
		} else {
			this.trackFilterSelection(filter);
			this.currentSelectedFilters.unshift(filter);
		}
	}

	updateSelectedFilters(filters: ISelectedFilter | ISelectedFilter[]) {
		// arrays only occur on mobile where multiple filters can be selected at once
		if (Array.isArray(filters)) {
			filters.forEach(filter => this.trackFilterSelection(filter));
			this.currentSelectedFilters = filters;
		} else {
			this.toggleSelectedFilters(filters);
			Vue.nextTick(() => {
				const element = this.$refs[filters.filter];
				if (isHTMLElement(element?.[0])) {
					element?.[0]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
				}
			});
		}
		this.handleJobSearch(!this.currentSelectedFilters?.length); // update filters if none is selected
	}

	trackFilterSelection(filter: ISelectedFilter) {
		this.$trackWebsiteEvent('set_jobsearch_filter', {
			filterType: filter.filter.split('-')?.[0],
			filterValue: filter.filter.slice(filter.filter.indexOf('-') + 1)
		});
	}

	async handleJobSearch(newSearch = false) {
		this.loading = true;

		try {
			// if location is focused, wait for change event
			if (isFocused(this.$refs.locationAutocomplete)) {
				if (hasBlur(this.$refs.locationAutocomplete)) {
					this.$refs.locationAutocomplete.blur();
				}

				const done = new Promise(resolve =>
					(this.$refs.locationAutocomplete as any as ComponentPublicInstance<any>)?.$on(
						'blur',
						resolve
					)
				);

				await Promise.race([done, new Promise(resolve => setTimeout(resolve, 3000))]); // wait for max 3sec
			}

			// if branch is focused, wait for change event
			if (isFocused(this.$refs.branchAutocomplete)) {
				if (hasBlur(this.$refs.branchAutocomplete)) {
					this.$refs.branchAutocomplete.blur();
				}

				const done = new Promise(resolve =>
					(this.$refs.branchAutocomplete as any as ComponentPublicInstance<any>).$on(
						'blur',
						resolve
					)
				);
				await Promise.race([done, new Promise(resolve => setTimeout(resolve, 3000))]); // wait for max 3sec
			}

			const payload: IJobSearchRequest = {
				branch: this.currentJobField || '',
				city: this.currentLocationName || '',
				filters: this.currentSelectedFilters.map(a => a.filter),
				isCompanyPage: this.isCompanyPage,
				radius: parseInt(this.currentRadius, 10) || 0,
				page: 1,
				clientInteraction: process.client
			};

			let reset = false;

			if (
				!this.currentJobField &&
				!this.currentLocationName &&
				this.currentSelectedFilters.length === 0
			) {
				reset = true;
			}

			const [result] = await Promise.all([
				this.loadJobsSearch(payload),
				new Promise<void>(resolve =>
					setTimeout(() => {
						resolve();
					}, 150)
				)
			]);

			// Push route to stack if beautified url is valid
			let resultListUrl = result.beautifiedUrl || (reset && '/jobs');

			// if there is no url, add query params
			if (!resultListUrl) {
				try {
					const queryParams = new URLSearchParams();
					if (result.selected?.branch?.name) {
						queryParams.append('branch', result.selected?.branch?.name);
					}
					if (result.selected?.city?.name) {
						queryParams.append('city', result.selected?.city?.name);
					}
					result.selected?.filters?.forEach(f => {
						queryParams.append('filters', f.name);
					});

					const queryParam = queryParams.toString();
					if (queryParam.length) {
						resultListUrl = `/jobs/search?${queryParams.toString()}`;
					} else {
						resultListUrl = `/jobs`;
					}
				} catch (err) {
					// ignore errors if url search params is not available
				}
			}

			if (result.selected?.city?.name && this.currentRadiusId) {
				const params = new URLSearchParams();
				params.append('radius', this.currentRadiusId.toString());
				resultListUrl += `${resultListUrl.includes('?') ? '&' : '?'}${params.toString()}`;
			}

			const matchedRoute =
				resultListUrl &&
				resultListUrl !== this.lastResultUrl &&
				this.$router.resolve(resultListUrl);

			if (resultListUrl) {
				this.lastResultUrl = resultListUrl;
			}

			if (matchedRoute && matchedRoute?.resolved?.name) {
				// use name instead of path (like it was before)
				// (reason why: https://stackoverflow.com/a/40681470 / https://router.vuejs.org/guide/essentials/navigation.html#navigate-to-a-different-location )
				this.$router.push({
					name: matchedRoute.resolved.name,
					query: { ...matchedRoute.resolved.query },
					params: {
						...matchedRoute.resolved.params,
						availableFilters: !newSearch && this.availableFilters, // pass currently available filters to new page (if newsearch is false)
						result
					}
				});
				return;
			}

			// filters are updated only when user initates a completely new search request
			if (newSearch || reset) {
				this.availableFilters = result.availableFilters;
			}
			this.relations = result.relations;
			if (result.jobListDebug) {
				this.jobListDebug = Object.freeze(result.jobListDebug);
			}
			this.pageCount = Math.ceil(result.relationCount / 15) || 1;
			this.currentPage = result.relationCount > 0 ? 1 : 0;
			this.initialJobField = this.currentJobField;
			this.initialLocationName = this.currentLocationName;
			this.enableNotifications = result.path?.pageType !== 'none';
			this.jobgone = false;

			this.apiData = result;
			if (result.otherTerms) {
				this.otherTerms = result.otherTerms;
				this.$store.commit('jobs/setOtherTerms', this.otherTerms);
			}
			this.error = false;
		} catch (err: any) {
			if (err?.response?.status === 404 && err?.response?.data?.code === 'INVALID_LOCATION') {
				this.error = { title: 'Ort konnte nicht gefunden werden' };
				if (err?.response.data?.alternatives?.length > 0) {
					this.error.alternatives = err?.response.data?.alternatives;
				}

				if (err?.response.data?.otherTerms) {
					this.otherTerms = err?.response.data?.otherTerms;
				}
				const branch = this.currentJobField || '';
				const city = this.currentLocationName || '';

				window.history.replaceState(
					window.history.state,
					this.pageTitle,
					`/jobs/search?branch=${branch}&city=${city}`
				);
			} else {
				this.error = err;
				this.$errorHandler.call(this, err);
			}
		} finally {
			this.$nextTick(() => {
				this.loading = false;
			});
		}
	}

	async loadJobData(jobNr: number, showDetailView = true) {
		this.loading = true;
		try {
			const {
				job,
				additionalJobData,
				additionalSettings,
				jobOverviewLps,
				similarJobs,
				rootFields
			} = await this.loadJobDetail({ jobNr });
			this.jobDetail = job;
			this.additionalJobData = additionalJobData;
			this.additionalSettings = additionalSettings;
			this.jobOverviewLps = jobOverviewLps;
			this.similarJobs = similarJobs;
			this.rootFields = rootFields;
			if (showDetailView) {
				this.triggerDetailView();
			}
		} catch (e) {
			console.error('Could not fetch jobdetail!');
			this.error = { title: 'Upps, der Job konnte nicht richtig geladen werden.' };
		} finally {
			this.loading = false;
		}
	}

	async handlePageNav(direction: 'next' | 'prev') {
		this.loading = true;
		if (direction === 'next') {
			this.currentPage += 1;
		} else {
			this.currentPage -= 1;
		}
		this.showCurrentPage = true;

		const result = await this.loadJobsSearch({
			branch: this.currentJobField ? this.currentJobField : '',
			city: this.currentLocationName ? this.currentLocationName : '',
			filters: this.currentSelectedFilters.map(a => a.filter),
			isCompanyPage: this.isCompanyPage,
			radius: parseInt(this.currentRadius, 10) || 0,
			page: this.currentPage
		});

		const jobs = result.relations.filter(j => j.type === 'job');

		this.$trackWebsiteEvent('click_joblist_pagination', {
			jobs: jobs.map(j => ({ _id: j.obj._id, jobNr: j.obj.jobNr })),
			is_logged_in: this.$store.state.login.loggedIn ? 1 : 0
		});
		// freeze objects for performance reason (they'll lose reactiviness)
		this.relations = Object.freeze(result.relations);
		this.$scrollTo(this.isCompanyPage ? '#jobs' : '#header', { offset: -68 });
		this.loading = false;
	}

	setupJobAlarm() {
		this.openForm('jobalarm');
		this.$store.commit('setJobFilter', this.jobAlarmFormData);
		if (this.$askPushPermission) {
			this.$askPushPermission(true);
		}
	}

	openForm(mode) {
		this.mode = mode;
		this.$nextTick(() => {
			this.jobPopupHandler?.handleClick();
		});
	}

	activateJobalarm(eventLabel: jobalarmEventLabel, useJobFields = false) {
		this.$trackWebsiteEvent('activate_job_alarm', { pageElement: eventLabel });
		this.currentEventLabel = eventLabel;
		this.useJobFields = useJobFields;
		this.setupJobAlarm();
	}

	handleSendStatus(message: string, _result?: any) {
		switch (message) {
			case 'success':
				if (this.mode === 'jobalarm') {
					this.$trackWebsiteEvent('activate_job_alarm_completed', {
						pageElement: this.currentEventLabel
					});
					this.$snack.success({
						text: 'Öffne dein E-Mail Postfach und bestätige den Job-Alarm.'
					});
					this.jobalarmTriggered = true;
				} else if (this.mode === 'sendjob') {
					this.$trackWebsiteEvent('send_job_completed', {});
					this.$snack.success({
						text: 'Job erfolgreich zugesendet!',
						button: 'Kostenlos registrieren',
						action: () => {
							window.location.href = '/app/login';
						}
					});
				} else if (this.mode === 'login') {
					this.$snack.success({
						text: 'Erfolgreich angemeldet!'
					});
				}
				break;
			case 'fail':
			default:
				if (this.mode === 'login') {
					this.$snack.danger({
						title: 'Fehlgeschlagen!',
						text: 'Beim Anmelden ist etwas schief gelaufen.'
					});
				} else {
					this.$snack.danger({
						title: 'Fehlgeschlagen!',
						text: 'Beim Versenden ist etwas schief gelaufen.'
					});
				}
				break;
		}
	}

	openFilters() {
		// disconnect observer before opening insidepage
		this.observer?.disconnect();
		if (this.$isMobile.any) {
			this.$nuxt.$loading.start();
			return import('~/components/insidepages/jobfilters.vue')
				.then(async JobFilters => {
					await this.$page.push(
						JobFilters,
						{
							availableFilters: this.availableFilters,
							currentSelectedFilters: this.currentSelectedFilters,
							isCompanyPage: this.isCompanyPage,
							company: this.company
						},
						{
							name: 'jobfilterpage',
							mode: 'modal',
							modalHeading: 'Jobs filtern',
							on: {
								apply: selectedFilters => {
									this.$page.goBack();
									this.updateSelectedFilters(selectedFilters);
								}
							},
							// reobserve elements after inside page is closed since references were deleted
							done: () => setTimeout(this.observeElements, 300)
						}
					);
					this.$nuxt.$loading.finish();
				})
				.catch(err => this.$errorHandler.call(this, err));
		}
		this.showFilters = !this.showFilters;
	}

	openJobSearch() {
		// disconnect observer before opening insidepage
		this.observer?.disconnect();
		if (this.$isMobile.any) {
			this.$nuxt.$loading.start();
			return import('~/components/insidepages/jobsearch.vue')
				.then(async JobSearch => {
					await this.$page.push(
						JobSearch,
						{
							valuePromise: this.ontologyKeywordSuggestion,
							fullwidth: 'always',
							currentLocationButton: true,
							resolveGeoCoords: this.resolveGeoCoords,
							currentJobField: this.currentJobField,
							currentLocationName: this.currentLocationName,
							currentRadius: this.currentRadius,
							currentRadiusOptions: this.radiusOptions
						},
						{
							name: 'jobsearch',
							pageTitle: 'Jobsuche',
							on: {
								search: (jobField, locationName, radius, radiusOptions) => {
									this.currentJobField = jobField;
									this.currentLocationName = locationName;
									this.currentRadius = radius;
									this.radiusOptions = radiusOptions;
									this.$page.goBack();
									this.handleJobSearch(true);
								}
							},
							// reobserve elements after inside page is closed since references were deleted
							done: () => setTimeout(this.observeElements, 300)
						}
					);
					this.$nuxt.$loading.finish();
				})
				.catch(err => this.$errorHandler.call(this, err));
		}
		this.showFilters = !this.showFilters;
	}

	mapSuggestionType(type: 'company' | 'keyword') {
		return type === 'company' ? 'Firma' : 'Job';
	}

	triggerDetailView() {
		if (!this.jobDetail) {
			console.warn('no job detail');
			return;
		}
		// tracking for remarketing
		const country = this.jobDetail.location?.countryCode
			? this.jobDetail.location.countryCode.replace(/\W/g, '').toUpperCase()
			: undefined;
		this.$trackWebsiteEvent('job_view', {
			name: this.jobDetail.name,
			jobNr: this.jobDetail.jobNr,
			jobId: this.jobDetail._id,
			topfields: this.rootFields,
			jobfields: this.jobDetail.fields.map(f => f.name),
			type: this.jobDetail.type,
			priorize: this.jobDetail.internal.trackingValue || 1,
			similarJobs: this.similarJobs?.map(j => ({ _id: j?.job?._id })),
			country,
			city: this.jobDetail.location?.city,
			is_logged_in: this.$store.state.login.loggedIn ? 1 : 0
		});
	}

	get savedMatches(): IAPIMatchForJobSeekerListEntry[] {
		return this.getAll;
	}

	async saveJob(job: IAPIJobPreviewForUser) {
		try {
			const relation = {
				type: 'job',
				obj: job
			};
			let { name } = job;
			if (name.trim().length > 30) {
				name = `${name.trim().slice(0, 30)}...`;
			}
			if (this.loggedIn) {
				if (this.getStatusOfRelation(relation) !== 'saved') {
					await this.saveRelation(relation);
					await this.$trackWebsiteEvent('save_job', {
						jobNr: job.jobNr,
						item_id: job._id,
						item_type: 'job'
					});
					this.$snack.success({
						text: `Der  Job "${name}" wurde gespeichert!`
					});
				} else if (this.getStatusOfRelation(relation) === 'saved') {
					const match = this.getMatchByRelation(relation);
					await this.deleteMatch({ match });
					this.$snack.success({
						text: `Der gespeicherte Job "${name}" wurde gelöscht!`
					});
				}
			} else {
				this.openForm('login');
				await this.$trackWebsiteEvent('save_job', {
					jobNr: job.jobNr,
					item_id: job._id,
					item_type: 'jobNotLoggedIn'
				});
			}
		} catch (err: any) {
			this.$snack.danger({
				text: 'Beim Speichern des Jobs ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
			});
		}
	}

	jobsearchFeedback(value) {
		// seperate tracking events because an issues with analytics
		switch (value) {
			case 0:
				this.$trackWebsiteEvent('search_result_usefulness_0', {});
				break;
			case 1:
				this.$trackWebsiteEvent('search_result_usefulness_1', {});
				break;
			case 2:
				this.$trackWebsiteEvent('search_result_usefulness_2', {});
				break;
			case 3:
				this.$trackWebsiteEvent('search_result_usefulness_3', {});
				break;
			default:
				this.$trackWebsiteEvent('search_result_usefulness_4', {});
				break;
		}
		this.$snack.success({
			text: 'Danke für deine Teilnahme an unserer Umfrage.'
		});
	}

	async calcGnc(gnc) {
		try {
			this.gncCalculation = await this.sendGnc({ ...gnc, locationUrl: 'job-list' });
			this.$trackWebsiteEvent('start_calculation', {});
		} catch (err: any) {
			this.$errorHandler.call(this, err);
		}
	}

	openJobsearch() {
		this.$router.push('/jobs');
		this.$trackWebsiteEvent('click_redirect', { url: '/jobs' });
	}

	async setFromRoute(from: 'startpage' | 'gnc' | 'default') {
		if (!this.$store.state.uistate.fromRoute) {
			this.$store.commit('uistate/updateFromRoute', from);
		}
	}

	get titleBefore(): string {
		let first = true;
		let titleBefore = '';
		if (this.apiData) {
			this.apiData.selected?.filters?.forEach(filter => {
				if (first && filter.titlePosition !== 'after') {
					titleBefore += convertNumber(filter.formatted);
					first = false;
				} else if (filter.titlePosition !== 'after') {
					titleBefore += `, ${convertNumber(filter.formatted)}`;
				}
			});
		}
		return titleBefore;
	}

	get titleAfter(): string {
		const titleAfter: string[] = [];
		if (this.apiData) {
			this.apiData.selected?.filters?.forEach(filter => {
				if (filter.titlePosition && filter.titlePosition === 'after') {
					titleAfter.push(filter.formatted);
				}
			});
		}
		return titleAfter.join(', ');
	}

	// Computed properties are defined here, so we can easily override them in the components extending the Mixin
	get pageTitle(): string {
		return '';
	}

	get pageDescription(): string {
		return '';
	}

	get socialsDescription(): string {
		return '';
	}

	get socialsTitle(): string {
		return '';
	}

	get socialsImage(): string {
		return '';
	}

	get hasAvailableFilters(): boolean {
		return this.availableFilters && Object.keys(this.availableFilters)?.length > 0;
	}

	get showOtherTermsSearches(): boolean {
		return this.otherTerms?.searches?.length > 0;
	}

	get showOtherTermsPlaces(): boolean {
		return this.otherTerms?.places?.length > 0;
	}

	get showOtherTermsKeywords(): boolean {
		return this.otherTerms?.keywords?.length > 0;
	}

	get showOtherTermsRefinedSearches(): boolean {
		return this.otherTerms?.refined?.length > 0;
	}

	get showOtherTermsLocationHierarchy(): boolean {
		return !!(
			this.otherTerms?.interlinking?.locationHierarchy &&
			this.otherTerms.interlinking.locationHierarchy.length > 0
		);
	}

	get showOtherTermsJobField(): boolean {
		return !!(
			this.otherTerms?.interlinking?.jobField && this.otherTerms.interlinking.jobField.length > 0
		);
	}

	// only return a maximum of 15 jobs per oage
	get maxPastJobs(): { job: IAPIJobPreviewForUser }[] | null {
		if (this.apiData?.pastJobs?.length) {
			return this.apiData.pastJobs.slice(0, 15 - this.relations.length);
		}
		return null;
	}

	get searchBarValue(): string {
		if (this.currentJobField || this.currentLocationName) {
			return `${this.currentJobField} Jobs
			${this.currentJobField && this.currentLocationName ? ' - ' : ''}${this.currentLocationName}`;
		}
		if (!this.currentSelectedFilters || this.currentSelectedFilters.length === 0) {
			return 'Beruf, Firma, Stichwort';
		}
		return '';
	}

	get jobIsSaved() {
		return this.getStatusOfRelation({ type: 'job', obj: this.jobDetail }) === 'saved';
	}

	get userImage(): string | undefined {
		return this.user?.images?.small;
	}

	get showSeoFooter(): boolean {
		return !!this.jobDetail;
	}

	get quickFilters(): ISelectedFilter[] {
		const quickFilters: ISelectedFilter[] = [];
		const includedJobtypes = ['jobtype-vollzeit', 'jobtype-teilzeit', 'jobtype-geringfügig'];
		const jobTypesFilter = this.availableFilters?.Anstellungsart?.filter(filter =>
			includedJobtypes.includes(filter.filter)
		).reverse();
		if (jobTypesFilter) {
			quickFilters.push(...jobTypesFilter);
		}

		const publishTime = this.availableFilters['Job online seit'];
		/* TODO: remove again when filter naming is changed in the database */
		const publishTimeFilter = publishTime
			?.map(item => {
				if (item.filter === 'publish-hours') {
					return { ...item, title: 'letzte 24 Stunden' };
				}
				if (item.filter === 'publish-days') {
					return { ...item, title: 'letzte 7 Tage' };
				}
				return item;
			})
			.reverse();
		if (publishTimeFilter) {
			quickFilters.push(...publishTimeFilter);
		}
		return quickFilters;
	}

	get currentRadiusId(): number | undefined {
		if (this.currentRadius && this.radiusOptions) {
			return this.radiusOptions.find(option => option?.value === this.currentRadius)?.id;
		}
		return undefined;
	}

	filterActive(filter: ISelectedFilter): boolean {
		return this.currentSelectedFilters.some(f => f.filter === filter.filter);
	}

	/* TODO: remove again when filter naming is changed in the database */
	shortenFilterTitles(filter: ISelectedFilter) {
		let filterTitle = '';
		switch (filter.filter) {
			case 'publish-hours':
				filterTitle = 'letzte 24 Stunden';
				break;
			case 'publish-days':
				filterTitle = 'letzte 7 Tage';
				break;
			case 'wage-0-520.0':
				filterTitle = 'bis 520 €';
				break;
			case 'wage-850.0-1200.0':
				filterTitle = '850 € - 1.200 €';
				break;
			case 'wage-1200.0-2500.0':
				filterTitle = '1.200 € - 2.500 €';
				break;
			case 'wage-2500.0-5300.0':
				filterTitle = '2.500 € - 5.300 €';
				break;
			default:
				filterTitle = filter.title;
				break;
		}
		if (filter.title.includes('in der Branche')) {
			filterTitle = filter.title.replace('in der Branche ', '');
		}

		return filterTitle.replace(/['"]+/g, '');
	}

	trackFilter(type?: string) {
		if (type) {
			this.$trackWebsiteEvent('click_quick_filter', { type });
		} else {
			this.$trackWebsiteEvent('click_filter', {});
		}
	}

	setQuickFilter(filter: ISelectedFilter) {
		this.updateSelectedFilters(filter);
		this.trackFilter(filter.filter);
	}
}
